/**
 * Documentation: the team's Markdown, in folders.
 *
 * People write here, upload files or whole folders, and agents write through the MCP server — the
 * same documents by the same rules: `.md` only, text only, no real keys or tokens. Bugs and SDDs
 * point at documents, and a document shows what points at it.
 *
 * Reading is for everyone in the organization; writing for developers and above. What a document
 * says is rendered by lib/markdown, which never hands the browser HTML.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type FormEvent, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileCode2,
  BookOpen,
  Bug as BugIcon,
  ChevronDown,
  ChevronRight,
  Download,
  FilePlus,
  FileText,
  Folder,
  FolderOpen,
  FolderPlus,
  FolderUp,
  ListChecks,
  MoveRight,
  Pencil,
  Search,
  Trash2,
  Upload,
  X,
  Pin,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { atLeast, getSession } from '../lib/auth'
import { useI18n } from '../lib/i18n'
import { formatRelative } from '../lib/format'
import { useDebounced } from '../lib/useDebounced'
import { outlineOf, parseMarkdown } from '../lib/markdown'
import { Markdown } from '../components/Markdown'
import { DocsAgentActions } from '../components/DocsAgentActions'
import { DocWorkMenu } from '../components/DocWorkMenu'
import { SendWorkDialog } from '../components/SendWorkDialog'
import {
  DOC_MAX_BYTES,
  ancestorsOf,
  asDocName,
  buildTree,
  byteLength,
  descendantsOf,
  filesFromDrop,
  filesFromInput,
  formatBytes,
  isValidDocName,
  isValidSegment,
  partitionUploads,
  uploadBatches,
  type PickedFile,
  type TreeNode,
} from '../lib/docsTree'
import { CopyButton, EmptyState, ErrorBanner, Segmented, Skeleton } from '../components/primitives'
import type { DocDetail, DocFolder, DocSummary, DocTree, DocUploadResult, WorkOrderKind } from '../lib/types'

type Dialog =
  | { kind: 'new-doc'; folderId: string | null }
  | { kind: 'new-folder'; parentId: string | null }
  | { kind: 'move-doc'; doc: DocDetail }
  | { kind: 'edit-folder'; folder: DocFolder }
  | { kind: 'delete-doc'; doc: DocDetail }
  | { kind: 'delete-folder'; folder: DocFolder }

interface UploadReport {
  running: boolean
  done: number
  total: number
  results: DocUploadResult[]
  skipped: Array<{ path: string; reason: string }>
}

const EXPANDED_KEY = 'devpulse:docs-expanded'

/** The message for a refusal, in the reader's language when the code is one of ours. */
function useErrorText(): (error: unknown) => string {
  const { t } = useI18n()
  return useCallback(
    (error: unknown) => {
      if (!(error instanceof ApiError)) return t('error.generic')
      if (error.code === 'contains_secret') {
        const kinds = /\(([^)]+)\)/.exec(error.message)?.[1]
        return t('docs.error.contains_secret', { kinds: kinds ?? '—' })
      }
      const known = ['invalid_name', 'invalid_path', 'invalid_content', 'name_taken', 'not_found', 'folder_not_empty', 'invalid_move', 'version_conflict', 'invalid_link']
      return error.code && known.includes(error.code) ? t(`docs.error.${error.code}`) : t(error.translationKey)
    },
    [t],
  )
}

/** Which folders are open, remembered per browser. */
function useExpanded(): [Set<string>, (update: (current: Set<string>) => Set<string>) => void] {
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(EXPANDED_KEY) ?? '[]') as string[])
    } catch {
      return new Set()
    }
  })
  useEffect(() => {
    try {
      localStorage.setItem(EXPANDED_KEY, JSON.stringify([...expanded]))
    } catch {
      // Private mode: the tree just forgets.
    }
  }, [expanded])
  return [expanded, setExpanded]
}

export default function Docs() {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const errorText = useErrorText()
  const canWrite = atLeast(getSession()?.role, 'Developer')

  const [params, setParams] = useSearchParams()
  const docId = params.get('doc')
  const pathParam = params.get('path')
  const anchor = params.get('at')

  const { data: tree, isLoading, error: treeError, refetch } = useQuery({
    queryKey: ['docs', 'tree'],
    queryFn: api.docs.tree,
    refetchInterval: 30_000,
  })
  const root = useMemo(() => (tree ? buildTree(tree) : null), [tree])

  const { data: doc, isLoading: docLoading, error: docError } = useQuery({
    queryKey: ['docs', 'doc', docId],
    queryFn: () => api.docs.get(docId!),
    enabled: Boolean(docId),
    retry: false,
  })

  const [folderId, setFolderId] = useState<string | null>(null)
  const [expanded, setExpanded] = useExpanded()
  const [search, setSearch] = useState('')
  const term = useDebounced(search.trim(), 250)
  const [dialog, setDialog] = useState<Dialog | null>(null)
  // Right-click on a document: send the agent to work on it.
  const [docMenu, setDocMenu] = useState<{ doc: DocSummary; x: number; y: number } | null>(null)
  const [sendWork, setSendWork] = useState<{ doc: DocSummary; kind: WorkOrderKind } | null>(null)
  const [draft, setDraft] = useState<string | null>(null)
  const [uploads, setUploads] = useState<UploadReport | null>(null)
  const [dragging, setDragging] = useState(false)
  const [treeOpen, setTreeOpen] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const folderInput = useRef<HTMLInputElement>(null)

  const { data: hits, isFetching: searching } = useQuery({
    queryKey: ['docs', 'search', term],
    queryFn: () => api.docs.search(term),
    enabled: term.length >= 2,
  })

  const dirty = draft !== null && doc !== undefined && draft !== doc.content

  // Leaving with unsaved text asks first — closing the tab as much as clicking elsewhere.
  useEffect(() => {
    if (!dirty) return
    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  const open = useCallback(
    (id: string | null, at?: string) => {
      if (dirty && !window.confirm(t('docs.discardChanges'))) return
      setDraft(null)
      setTreeOpen(false)
      const next = new URLSearchParams()
      if (id) next.set('doc', id)
      if (at) next.set('at', at)
      setParams(next)
    },
    [dirty, setParams, t],
  )

  /** A link by path — from inside a document, or shared — opens the document it names. */
  const openPath = useCallback(
    (path: string, at?: string) => {
      const found = tree?.docs.find((d) => d.path === path)
      if (found) open(found.id, at)
      else {
        const next = new URLSearchParams()
        next.set('path', path)
        setParams(next)
      }
    },
    [tree, open, setParams],
  )

  useEffect(() => {
    if (!pathParam || !tree) return
    const found = tree.docs.find((d) => d.path === pathParam)
    if (!found) return
    const next = new URLSearchParams()
    next.set('doc', found.id)
    if (anchor) next.set('at', anchor)
    setParams(next, { replace: true })
  }, [pathParam, tree, anchor, setParams])

  // Opening a document opens the tree down to it, and new things go next to it.
  const openFolderId = doc?.folderId ?? null
  const openDocId = doc?.id
  useEffect(() => {
    if (!openDocId || !tree) return
    setFolderId(openFolderId)
    const chain = ancestorsOf(tree.folders, openFolderId)
    setExpanded((current) => {
      if (chain.every((folder) => current.has(folder.id))) return current
      const next = new Set(current)
      chain.forEach((folder) => next.add(folder.id))
      return next
    })
  }, [openDocId, openFolderId, tree, setExpanded])

  const content = doc?.content
  useEffect(() => {
    if (content === undefined) return
    if (anchor) document.getElementById(`doc-${anchor}`)?.scrollIntoView({ block: 'start' })
    else document.querySelector('.docs__main')?.scrollTo({ top: 0 })
  }, [content, anchor])

  const currentFolder = folderId ? tree?.folders.find((f) => f.id === folderId) ?? null : null

  const refreshTree = useCallback(() => void queryClient.invalidateQueries({ queryKey: ['docs'] }), [queryClient])

  const toggle = useCallback(
    (id: string) =>
      setExpanded((current) => {
        const next = new Set(current)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      }),
    [setExpanded],
  )

  // ------------------------------------------------------------------ uploads

  const upload = useCallback(
    async (picked: PickedFile[]) => {
      if (picked.length === 0) return
      const { markdown, skipped } = partitionUploads(picked)
      const target = currentFolder?.path ?? null
      const results: DocUploadResult[] = []
      setUploads({ running: markdown.length > 0, done: 0, total: markdown.length, results, skipped })

      let done = 0
      for (const batch of uploadBatches(markdown)) {
        try {
          results.push(...(await api.docs.upload(batch, target)))
        } catch (error) {
          const reason = error instanceof ApiError ? error.code ?? 'error' : 'error'
          results.push(...batch.map((file) => ({ path: file.path, status: 'rejected' as const, reason })))
        }
        done += batch.length
        setUploads({ running: true, done, total: markdown.length, results: [...results], skipped })
      }

      setUploads({ running: false, done: markdown.length, total: markdown.length, results: [...results], skipped })
      refreshTree()
      if (target) setExpanded((current) => new Set([...current, ...(currentFolder ? [currentFolder.id] : [])]))
    },
    [currentFolder, refreshTree, setExpanded],
  )

  const onDrop = async (event: DragEvent): Promise<void> => {
    event.preventDefault()
    setDragging(false)
    if (!canWrite) return
    await upload(await filesFromDrop(event.dataTransfer))
  }

  // ------------------------------------------------------------------ the page

  let main: ReactNode
  if (docId && docLoading) main = <Skeleton height={420} />
  else if (docId && docError)
    main = (
      <EmptyState
        title={t('docs.notFound')}
        body={t('docs.notFoundBody')}
        action={<button type="button" className="btn btn--sm" onClick={() => open(null)}>{t('docs.backToDocs')}</button>}
      />
    )
  else if (doc && draft !== null)
    main = (
      <Editor
        doc={doc}
        draft={draft}
        onChange={setDraft}
        onDone={(saved) => {
          if (saved) queryClient.setQueryData(['docs', 'doc', saved.id], saved)
          setDraft(null)
          refreshTree()
        }}
      />
    )
  else if (doc)
    main = (
      <DocView
        doc={doc}
        tree={tree}
        canWrite={canWrite}
        onEdit={() => setDraft(doc.content)}
        onDialog={setDialog}
        onOpenPath={openPath}
        onSelectFolder={(id) => {
          setFolderId(id)
          setTreeOpen(true)
        }}
      />
    )
  else if (pathParam && tree && !tree.docs.some((d) => d.path === pathParam))
    main = (
      <EmptyState
        title={t('docs.notFound')}
        body={t('docs.pathMissing', { path: pathParam })}
        action={
          canWrite ? (
            <button type="button" className="btn btn--sm btn--primary" onClick={() => setDialog({ kind: 'new-doc', folderId: null })}>
              {t('docs.newDoc')}
            </button>
          ) : undefined
        }
      />
    )
  else if (tree && tree.docs.length === 0)
    main = (
      <div className="panel docs-empty">
        <EmptyState title={t('docs.empty.title')} body={t('docs.empty.body')} />
        <p className="small muted docs-empty__agent">
          <BookOpen size={14} aria-hidden /> {t('docs.empty.agent')}
        </p>
      </div>
    )
  else if (tree) main = <Welcome tree={tree} onOpen={(id) => open(id)} />
  else main = <Skeleton height={320} />

  return (
    <div className={`docs${doc ? ' docs--reading' : ''}${treeOpen ? ' docs--tree-open' : ''}`}>
      <aside
        className={`docs__side panel${dragging ? ' docs__side--drop' : ''}`}
        onDragOver={(event) => {
          if (!canWrite || !event.dataTransfer.types.includes('Files')) return
          event.preventDefault()
          setDragging(true)
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false)
        }}
        onDrop={(event) => void onDrop(event)}
      >
        <button type="button" className="btn btn--sm btn--ghost docs__side-toggle" onClick={() => setTreeOpen((value) => !value)} aria-expanded={treeOpen}>
          {treeOpen ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
          {t('docs.browse')}
        </button>

        <div className="docs__side-body">
          <label className="docs__search">
            <Search size={14} aria-hidden />
            <input
              className="input"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('docs.searchPlaceholder')}
              aria-label={t('docs.search')}
            />
          </label>

          {canWrite && (
            <div className="docs__actions">
              <button type="button" className="btn btn--sm btn--primary" onClick={() => setDialog({ kind: 'new-doc', folderId })}>
                <FilePlus size={13} aria-hidden /> {t('docs.newDoc')}
              </button>
              <button type="button" className="btn btn--sm" onClick={() => setDialog({ kind: 'new-folder', parentId: folderId })}>
                <FolderPlus size={13} aria-hidden /> {t('docs.newFolder')}
              </button>
              <button type="button" className="btn btn--sm" onClick={() => fileInput.current?.click()}>
                <Upload size={13} aria-hidden /> {t('docs.uploadFiles')}
              </button>
              <button type="button" className="btn btn--sm" onClick={() => folderInput.current?.click()}>
                <FolderUp size={13} aria-hidden /> {t('docs.uploadFolder')}
              </button>
              <input
                ref={fileInput}
                type="file"
                accept=".md,text/markdown"
                multiple
                hidden
                data-testid="docs-file-input"
                onChange={(event) => {
                  const files = event.target.files ? filesFromInput(event.target.files) : []
                  event.target.value = ''
                  void upload(files)
                }}
              />
              <input
                ref={folderInput}
                type="file"
                multiple
                hidden
                data-testid="docs-folder-input"
                {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
                onChange={(event) => {
                  const files = event.target.files ? filesFromInput(event.target.files) : []
                  event.target.value = ''
                  void upload(files)
                }}
              />
            </div>
          )}

          {/* Both of these read the documentation, so they belong beside it. */}
          {canWrite && <DocsAgentActions />}

          {/* The repository's own files, read-only (41, A14): a door here instead of an eighth tab. */}
          <Link to="/project/editor" className="btn btn--ghost btn--sm docs__editor-link">
            <FileCode2 size={13} /> {t('docs.openRepoFiles')}
          </Link>

          {canWrite && (
            <span className="tiny muted docs__target">
              {t('docs.target', { folder: currentFolder ? currentFolder.path : t('docs.root') })}
            </span>
          )}

          {treeError ? (
            <ErrorBanner message={errorText(treeError)} onRetry={() => void refetch()} retryLabel={t('common.retry')} />
          ) : isLoading || !root ? (
            <Skeleton height={200} />
          ) : term.length >= 2 ? (
            <SearchResults term={term} hits={hits} loading={searching} onOpen={(id) => open(id)} />
          ) : (
            <nav className="docs-tree" aria-label={t('docs.title')}>
              <button
                type="button"
                className={`docs-tree__row docs-tree__root${folderId === null ? ' is-current' : ''}`}
                onClick={() => setFolderId(null)}
              >
                <BookOpen size={14} aria-hidden /> {t('docs.root')}
              </button>
              <FolderChildren
                node={root}
                depth={0}
                expanded={expanded}
                currentFolderId={folderId}
                openDocId={docId}
                canWrite={canWrite}
                onToggle={toggle}
                onSelectFolder={setFolderId}
                onOpenDoc={(id) => open(id)}
                onDialog={setDialog}
                onDocMenu={canWrite ? (doc, x, y) => setDocMenu({ doc, x, y }) : undefined}
              />
            </nav>
          )}

          {canWrite && <p className="tiny muted docs__drop-hint">{t('docs.dropHint')}</p>}
        </div>
      </aside>

      <section className="docs__main">
        {uploads && <UploadPanel report={uploads} onClose={() => setUploads(null)} />}
        {main}
      </section>

      {docMenu && (
        <DocWorkMenu
          name={docMenu.doc.name}
          x={docMenu.x}
          y={docMenu.y}
          onPick={(kind) => setSendWork({ doc: docMenu.doc, kind })}
          pinned={docMenu.doc.pinned}
          onTogglePin={() => {
            const target = docMenu.doc
            void api.docs.setPinned(target.id, !target.pinned).then(() => {
              void queryClient.invalidateQueries({ queryKey: ['docs'] })
            })
          }}
          onClose={() => setDocMenu(null)}
        />
      )}
      {sendWork && <SendWorkDialog kind={sendWork.kind} docPath={sendWork.doc.path} onClose={() => setSendWork(null)} />}

      {dialog && tree && (
        <DocDialog
          dialog={dialog}
          tree={tree}
          onClose={() => setDialog(null)}
          onCreatedDoc={(created) => {
            setDialog(null)
            refreshTree()
            open(created.id)
            setDraft(created.content)
          }}
          onChanged={(selectFolder) => {
            setDialog(null)
            refreshTree()
            if (selectFolder !== undefined) {
              setFolderId(selectFolder)
              if (selectFolder) setExpanded((current) => new Set([...current, selectFolder]))
            }
          }}
          onDeletedDoc={() => {
            setDialog(null)
            refreshTree()
            open(null)
          }}
          locale={locale}
        />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------- tree */

function FolderChildren({
  node,
  depth,
  expanded,
  currentFolderId,
  openDocId,
  canWrite,
  onToggle,
  onSelectFolder,
  onOpenDoc,
  onDialog,
  onDocMenu,
}: {
  node: TreeNode
  depth: number
  expanded: Set<string>
  currentFolderId: string | null
  openDocId: string | null
  canWrite: boolean
  onToggle: (id: string) => void
  onSelectFolder: (id: string) => void
  onOpenDoc: (id: string) => void
  onDialog: (dialog: Dialog) => void
  onDocMenu?: (doc: DocSummary, x: number, y: number) => void
}) {
  const { t } = useI18n()
  const indent = { paddingLeft: 8 + depth * 14 }

  return (
    <ul className="docs-tree__list">
      {node.folders.map((child) => {
        const folder = child.folder!
        const isOpen = expanded.has(folder.id)
        return (
          <li key={folder.id}>
            <div className={`docs-tree__row docs-tree__row--folder${currentFolderId === folder.id ? ' is-current' : ''}`} style={indent}>
              <button
                type="button"
                className="docs-tree__label"
                aria-expanded={isOpen}
                onClick={() => {
                  onToggle(folder.id)
                  onSelectFolder(folder.id)
                }}
              >
                {isOpen ? <ChevronDown size={13} aria-hidden /> : <ChevronRight size={13} aria-hidden />}
                {isOpen ? <FolderOpen size={14} aria-hidden /> : <Folder size={14} aria-hidden />}
                <span className="truncate">{folder.name}</span>
              </button>
              {canWrite && (
                <span className="docs-tree__tools">
                  <IconButton label={t('docs.newDocHere')} onClick={() => onDialog({ kind: 'new-doc', folderId: folder.id })}>
                    <FilePlus size={13} />
                  </IconButton>
                  <IconButton label={t('docs.newFolderHere')} onClick={() => onDialog({ kind: 'new-folder', parentId: folder.id })}>
                    <FolderPlus size={13} />
                  </IconButton>
                  <IconButton label={t('docs.editFolder')} onClick={() => onDialog({ kind: 'edit-folder', folder })}>
                    <Pencil size={13} />
                  </IconButton>
                  <IconButton label={t('docs.deleteFolder')} onClick={() => onDialog({ kind: 'delete-folder', folder })}>
                    <Trash2 size={13} />
                  </IconButton>
                </span>
              )}
            </div>
            {isOpen && (
              <FolderChildren
                node={child}
                depth={depth + 1}
                expanded={expanded}
                currentFolderId={currentFolderId}
                openDocId={openDocId}
                canWrite={canWrite}
                onToggle={onToggle}
                onSelectFolder={onSelectFolder}
                onOpenDoc={onOpenDoc}
                onDialog={onDialog}
                onDocMenu={onDocMenu}
              />
            )}
          </li>
        )
      })}
      {node.docs.map((doc) => (
        <li key={doc.id}>
          <Link
            to={`/project/docs?doc=${doc.id}`}
            className={`docs-tree__row docs-tree__row--doc${openDocId === doc.id ? ' is-open' : ''}`}
            style={{ paddingLeft: 8 + depth * 14 + 13 }}
            title={doc.title}
            onClick={(event) => {
              event.preventDefault()
              onOpenDoc(doc.id)
            }}
            onContextMenu={
              onDocMenu
                ? (event) => {
                    event.preventDefault()
                    onDocMenu(doc, event.clientX, event.clientY)
                  }
                : undefined
            }
          >
            <FileText size={14} aria-hidden />
            <span className="truncate">{doc.name}</span>
            {doc.pinned && <Pin size={11} className="docs-tree__pin" aria-label={t('pinned.badge')} />}
          </Link>
        </li>
      ))}
      {depth > 0 && node.folders.length === 0 && node.docs.length === 0 && (
        <li className="tiny muted docs-tree__empty" style={{ paddingLeft: 8 + depth * 14 + 13 }}>
          {t('docs.emptyFolder')}
        </li>
      )}
    </ul>
  )
}

function IconButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" className="docs-tree__tool" onClick={onClick} title={label} aria-label={label}>
      {children}
    </button>
  )
}

function SearchResults({
  term,
  hits,
  loading,
  onOpen,
}: {
  term: string
  hits: Array<{ id: string; path: string; title: string; snippet: string }> | undefined
  loading: boolean
  onOpen: (id: string) => void
}) {
  const { t } = useI18n()
  if (!hits) return loading ? <Skeleton height={120} /> : null
  if (hits.length === 0) return <p className="small muted">{t('docs.searchNone', { term })}</p>

  return (
    <ul className="docs-hits">
      {hits.map((hit) => (
        <li key={hit.id}>
          <button type="button" className="docs-hits__item" onClick={() => onOpen(hit.id)}>
            <span className="small bold truncate">{hit.title}</span>
            <span className="tiny mono muted truncate">{hit.path}</span>
            <span className="tiny muted docs-hits__snippet">
              <Highlight text={hit.snippet} term={term} />
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}

/** The search term marked in a snippet — as text, split and wrapped, never as HTML. */
function Highlight({ text, term }: { text: string; term: string }) {
  const lower = text.toLowerCase()
  const needle = term.toLowerCase()
  const parts: ReactNode[] = []
  let from = 0
  for (let at = lower.indexOf(needle); at >= 0 && needle; at = lower.indexOf(needle, from)) {
    parts.push(text.slice(from, at), <mark key={at}>{text.slice(at, at + needle.length)}</mark>)
    from = at + needle.length
  }
  parts.push(text.slice(from))
  return <>{parts}</>
}

/* -------------------------------------------------------------------- reading */

function DocView({
  doc,
  tree,
  canWrite,
  onEdit,
  onDialog,
  onOpenPath,
  onSelectFolder,
}: {
  doc: DocDetail
  tree: DocTree | undefined
  canWrite: boolean
  onEdit: () => void
  onDialog: (dialog: Dialog) => void
  onOpenPath: (path: string, anchor?: string) => void
  onSelectFolder: (id: string | null) => void
}) {
  const { t, locale } = useI18n()
  const outline = useMemo(() => outlineOf(parseMarkdown(doc.content)).filter((entry) => entry.level <= 3), [doc.content])
  const crumbs = tree ? ancestorsOf(tree.folders, doc.folderId) : []
  const shareLink = `${window.location.origin}/docs?doc=${doc.id}`

  const download = async (): Promise<void> => {
    const blob = await api.docs.download(doc.id)
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = doc.name
    anchor.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  const jump = (id: string): void => document.getElementById(`doc-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })

  return (
    <article className="panel docs-view">
      <header className="docs-view__header">
        <nav className="docs-view__crumbs tiny" aria-label={t('docs.location')}>
          <button type="button" className="docs-view__crumb" onClick={() => onSelectFolder(null)}>
            {t('docs.root')}
          </button>
          {crumbs.map((folder) => (
            <span key={folder.id} className="row row--tight">
              <ChevronRight size={11} aria-hidden className="muted" />
              <button type="button" className="docs-view__crumb" onClick={() => onSelectFolder(folder.id)}>
                {folder.name}
              </button>
            </span>
          ))}
          <ChevronRight size={11} aria-hidden className="muted" />
          <span className="mono">{doc.name}</span>
        </nav>

        <div className="docs-view__actions">
          {canWrite && (
            <button type="button" className="btn btn--sm btn--primary" onClick={onEdit}>
              <Pencil size={13} aria-hidden /> {t('common.edit')}
            </button>
          )}
          {canWrite && (
            <button type="button" className="btn btn--sm" onClick={() => onDialog({ kind: 'move-doc', doc })}>
              <MoveRight size={13} aria-hidden /> {t('docs.move')}
            </button>
          )}
          <button type="button" className="btn btn--sm" onClick={() => void download()}>
            <Download size={13} aria-hidden /> {t('docs.download')}
          </button>
          <CopyButton text={shareLink} copyLabel={t('docs.copyLink')} copiedLabel={t('common.copied')} />
          {canWrite && (
            <button type="button" className="btn btn--sm btn--ghost docs-view__delete" onClick={() => onDialog({ kind: 'delete-doc', doc })}>
              <Trash2 size={13} aria-hidden /> {t('common.delete')}
            </button>
          )}
        </div>

        <span className="tiny muted">
          {t('docs.meta', {
            version: doc.version,
            who: doc.updatedBy ?? t('common.unknown'),
            when: formatRelative(doc.updatedAt, locale),
            size: formatBytes(doc.sizeBytes),
          })}
        </span>
      </header>

      {outline.length >= 3 && (
        <details className="docs-view__outline-compact">
          <summary className="small">{t('docs.outline')}</summary>
          <Outline outline={outline} onJump={jump} />
        </details>
      )}

      <div className={`docs-view__body${outline.length >= 3 ? ' docs-view__body--outlined' : ''}`}>
        <Markdown source={doc.content} path={doc.path} onOpenDoc={onOpenPath} />
        {outline.length >= 3 && (
          <aside className="docs-view__outline">
            <span className="field__label">{t('docs.outline')}</span>
            <Outline outline={outline} onJump={jump} />
          </aside>
        )}
      </div>

      <LinksPanel doc={doc} canWrite={canWrite} />
    </article>
  )
}

function Outline({ outline, onJump }: { outline: Array<{ id: string; level: number; text: string }>; onJump: (id: string) => void }) {
  return (
    <ul className="docs-outline">
      {outline.map((entry) => (
        <li key={entry.id} style={{ paddingLeft: (entry.level - 1) * 10 }}>
          <a
            href={`#doc-${entry.id}`}
            onClick={(event) => {
              event.preventDefault()
              onJump(entry.id)
            }}
          >
            {entry.text}
          </a>
        </li>
      ))}
    </ul>
  )
}

/** What points at this document, and — for writers — pointing a bug or an SDD at it. */
function LinksPanel({ doc, canWrite }: { doc: DocDetail; canWrite: boolean }) {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const errorText = useErrorText()
  const [kind, setKind] = useState<'task' | 'spec'>('task')
  const [value, setValue] = useState('')
  const typed = useDebounced(value.trim(), 250)

  const { data: specs } = useQuery({
    queryKey: ['specs', 'for-docs'],
    queryFn: () => api.specs.list(),
    enabled: canWrite && kind === 'spec',
    staleTime: 60_000,
  })
  const { data: tasks } = useQuery({
    queryKey: ['tasks', 'for-docs', typed],
    queryFn: () => api.tasks.list({ search: typed, pageSize: 20 }),
    enabled: canWrite && kind === 'task' && typed.length >= 2,
  })

  const refresh = (updated?: DocDetail): void => {
    if (updated) queryClient.setQueryData(['docs', 'doc', doc.id], updated)
    void queryClient.invalidateQueries({ queryKey: ['docs'] })
  }

  const link = useMutation({
    mutationFn: () => api.docs.link(doc.id, kind === 'task' ? { taskExternalId: value.trim() } : { specificationKey: value.trim() }),
    onSuccess: () => {
      setValue('')
      refresh()
    },
  })
  const unlink = useMutation({
    mutationFn: (linkId: string) => api.docs.unlink(doc.id, linkId),
    onSuccess: () => refresh(),
  })

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    if (value.trim()) link.mutate()
  }

  return (
    <section className="docs-links">
      <span className="field__label">{t('docs.links.title')}</span>
      {doc.links.length === 0 ? (
        <span className="tiny muted">{t('docs.links.none')}</span>
      ) : (
        <ul className="docs-links__list">
          {doc.links.map((item) => {
            // The API leaves empty fields out, so "no task" arrives as undefined, not null.
            const isTask = Boolean(item.taskId)
            const label = isTask ? item.taskExternalId : item.specificationKey
            const title = isTask ? item.taskTitle : item.specificationTitle
            const to = isTask
              ? item.taskType === 'Bug'
                ? `/track/bugs?bug=${item.taskId}`
                : `/project/tasks?search=${encodeURIComponent(item.taskExternalId ?? '')}`
              : `/project/specs?spec=${item.specificationId}`
            return (
              <li key={item.id} className="docs-links__item">
                {isTask ? (item.taskType === 'Bug' ? <BugIcon size={13} aria-hidden /> : <ListChecks size={13} aria-hidden />) : <FileText size={13} aria-hidden />}
                {label ? (
                  <Link to={to} className="mono small">
                    {label}
                  </Link>
                ) : (
                  <span className="tiny muted">{t('docs.links.gone')}</span>
                )}
                <span className="small truncate">{title ?? ''}</span>
                {!isTask && item.specificationKind && <span className="tiny muted">{item.specificationKind === 'Prd' ? 'PRD' : 'SDD'}</span>}
                <div className="spacer" />
                {canWrite && (
                  <button
                    type="button"
                    className="docs-tree__tool"
                    title={t('docs.links.remove')}
                    aria-label={t('docs.links.remove')}
                    onClick={() => unlink.mutate(item.id)}
                    disabled={unlink.isPending}
                  >
                    <X size={13} />
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {canWrite && (
        <form className="docs-links__add" onSubmit={submit}>
          <Segmented
            value={kind}
            options={[
              { value: 'task', label: t('docs.links.task') },
              { value: 'spec', label: t('docs.links.spec') },
            ]}
            onChange={(next) => {
              setKind(next)
              setValue('')
              link.reset()
            }}
          />
          <input
            className="input"
            list="docs-link-options"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={kind === 'task' ? t('docs.links.taskPlaceholder') : t('docs.links.specPlaceholder')}
            aria-label={kind === 'task' ? t('docs.links.task') : t('docs.links.spec')}
          />
          <datalist id="docs-link-options">
            {kind === 'task'
              ? tasks?.items.map((task) => <option key={task.id} value={task.externalId}>{task.title}</option>)
              : specs?.specs.map((spec) => <option key={spec.id} value={spec.key}>{spec.title}</option>)}
          </datalist>
          <button type="submit" className="btn btn--sm" disabled={!value.trim() || link.isPending}>
            {t('docs.links.add')}
          </button>
          {link.error && <span className="field__error">{errorText(link.error)}</span>}
        </form>
      )}
    </section>
  )
}

function Welcome({ tree, onOpen }: { tree: DocTree; onOpen: (id: string) => void }) {
  const { t, locale } = useI18n()
  const recent = useMemo(() => [...tree.docs].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 10), [tree.docs])

  return (
    <div className="panel docs-welcome">
      <header className="panel__header">
        <h2 className="panel__title">{t('docs.recent')}</h2>
        <div className="spacer" />
        <span className="tiny muted">{t('docs.count', { count: tree.docs.length })}</span>
      </header>
      <ul className="docs-welcome__list">
        {recent.map((doc) => (
          <li key={doc.id}>
            <button type="button" className="docs-welcome__item" onClick={() => onOpen(doc.id)}>
              <FileText size={15} aria-hidden className="muted" />
              <span className="stack" style={{ gap: 0, minWidth: 0 }}>
                <span className="small bold truncate">{doc.title}</span>
                <span className="tiny mono muted truncate">{doc.path}</span>
              </span>
              <span className="spacer" />
              <span className="tiny muted docs-welcome__when">
                {doc.updatedBy ?? ''} · {formatRelative(doc.updatedAt, locale)}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <p className="tiny muted docs-welcome__agent">
        <BookOpen size={13} aria-hidden /> {t('docs.empty.agent')}
      </p>
    </div>
  )
}

/* -------------------------------------------------------------------- writing */

type EditorMode = 'write' | 'preview' | 'split'

function Editor({
  doc,
  draft,
  onChange,
  onDone,
}: {
  doc: DocDetail
  draft: string
  onChange: (text: string) => void
  onDone: (saved: DocDetail | null) => void
}) {
  const { t } = useI18n()
  const errorText = useErrorText()
  const [mode, setMode] = useState<EditorMode>(() => (window.matchMedia?.('(min-width: 1100px)').matches ? 'split' : 'write'))
  const [conflict, setConflict] = useState(false)
  const bytes = byteLength(draft)
  const tooBig = bytes > DOC_MAX_BYTES
  const dirty = draft !== doc.content

  const save = useMutation({
    mutationFn: (expectedVersion: number) => api.docs.update(doc.id, { content: draft, expectedVersion }),
    onSuccess: (saved) => onDone(saved),
    onError: (error) => setConflict(error instanceof ApiError && error.code === 'version_conflict'),
  })

  // Somebody saved in between: theirs is kept unless this person chooses to replace it.
  const overwrite = async (): Promise<void> => {
    const latest = await api.docs.get(doc.id)
    setConflict(false)
    save.mutate(latest.version)
  }

  const trySave = (): void => {
    if (dirty && !tooBig && !save.isPending) save.mutate(doc.version)
  }

  const cancel = (): void => {
    if (dirty && !window.confirm(t('docs.discardChanges'))) return
    onDone(null)
  }

  return (
    <article className="panel docs-editor">
      <header className="docs-editor__bar">
        <span className="small mono truncate docs-editor__path">{doc.path}</span>
        <Segmented
          value={mode}
          options={[
            { value: 'write', label: t('docs.mode.write') },
            { value: 'preview', label: t('docs.mode.preview') },
            { value: 'split', label: t('docs.mode.split') },
          ]}
          onChange={setMode}
        />
        <span className={`tiny ${tooBig ? 'docs-editor__over' : 'muted'}`}>
          {formatBytes(bytes)} / {formatBytes(DOC_MAX_BYTES)}
        </span>
        <div className="spacer" />
        <button type="button" className="btn btn--sm btn--ghost" onClick={cancel}>
          {t('common.cancel')}
        </button>
        <button type="button" className="btn btn--sm btn--primary" onClick={trySave} disabled={!dirty || tooBig || save.isPending}>
          {save.isPending ? t('common.saving') : t('common.save')}
        </button>
      </header>

      {conflict ? (
        <div className="docs-notice docs-notice--warn" role="alert">
          <span className="small">{t('docs.conflict')}</span>
          <div className="row row--wrap">
            <CopyButton text={draft} copyLabel={t('docs.copyMine')} copiedLabel={t('common.copied')} />
            <button type="button" className="btn btn--sm" onClick={() => onDone(null)}>
              {t('docs.conflictReload')}
            </button>
            <button type="button" className="btn btn--sm btn--danger" onClick={() => void overwrite()}>
              {t('docs.conflictOverwrite')}
            </button>
          </div>
        </div>
      ) : (
        save.error && (
          <div className="docs-notice docs-notice--danger" role="alert">
            <span className="small">{errorText(save.error)}</span>
          </div>
        )
      )}

      <div className={`docs-editor__panes docs-editor__panes--${mode}`}>
        {mode !== 'preview' && (
          <textarea
            className="docs-editor__text"
            value={draft}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => {
              if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
                event.preventDefault()
                trySave()
              }
            }}
            spellCheck={false}
            aria-label={t('docs.editor')}
            autoFocus
          />
        )}
        {mode !== 'write' && (
          <div className="docs-editor__preview">
            <Markdown source={draft} path={doc.path} idPrefix="preview-" />
          </div>
        )}
      </div>
      <p className="tiny muted docs-editor__hint">{t('docs.editorHint')}</p>
    </article>
  )
}

/* -------------------------------------------------------------------- uploads */

function UploadPanel({ report, onClose }: { report: UploadReport; onClose: () => void }) {
  const { t } = useI18n()
  const created = report.results.filter((r) => r.status === 'created').length
  const updated = report.results.filter((r) => r.status === 'updated').length
  const refused = [
    ...report.results.filter((r) => r.status === 'rejected').map((r) => ({ path: r.path, reason: r.reason ?? 'error' })),
    ...report.skipped,
  ]
  const reasonText = (reason: string): string =>
    ['not_markdown', 'too_large', 'not_text', 'invalid_name', 'invalid_path', 'invalid_content', 'contains_secret', 'name_taken'].includes(reason)
      ? t(`docs.upload.reason.${reason}`)
      : t('docs.upload.reason.error')

  return (
    <div className="panel docs-upload" role="status">
      <header className="panel__header">
        <h2 className="panel__title">
          {report.running ? t('docs.upload.running', { done: report.done, total: report.total }) : t('docs.upload.done')}
        </h2>
        <div className="spacer" />
        {!report.running && (
          <button type="button" className="btn btn--sm btn--ghost btn--icon" onClick={onClose} aria-label={t('common.close')}>
            <X size={14} />
          </button>
        )}
      </header>
      <div className="panel__body stack stack--tight">
        <span className="small">{t('docs.upload.summary', { created, updated, refused: refused.length })}</span>
        {refused.length > 0 && (
          <ul className="docs-upload__list">
            {refused.slice(0, 50).map((item, index) => (
              <li key={`${item.path}-${index}`} className="tiny">
                <span className="mono">{item.path}</span> — <span className="muted">{reasonText(item.reason)}</span>
              </li>
            ))}
            {refused.length > 50 && <li className="tiny muted">{t('docs.upload.more', { count: refused.length - 50 })}</li>}
          </ul>
        )}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------- dialogs */

function DocDialog({
  dialog,
  tree,
  onClose,
  onCreatedDoc,
  onChanged,
  onDeletedDoc,
  locale,
}: {
  dialog: Dialog
  tree: DocTree
  onClose: () => void
  onCreatedDoc: (doc: DocDetail) => void
  /** With a folder id, that folder becomes the current one; null means the root. */
  onChanged: (selectFolder?: string | null) => void
  onDeletedDoc: () => void
  locale: string
}) {
  const { t } = useI18n()
  const errorText = useErrorText()

  const initialName =
    dialog.kind === 'move-doc' ? dialog.doc.name : dialog.kind === 'edit-folder' ? dialog.folder.name : ''
  // `?? null`: the API leaves empty fields out, and a folder at the root has no parent at all.
  const initialFolder =
    (dialog.kind === 'new-doc'
      ? dialog.folderId
      : dialog.kind === 'new-folder'
        ? dialog.parentId
        : dialog.kind === 'move-doc'
          ? dialog.doc.folderId
          : dialog.kind === 'edit-folder'
            ? dialog.folder.parentId
            : null) ?? null

  const [name, setName] = useState(initialName)
  const [folder, setFolder] = useState<string | null>(initialFolder)

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const excluded = dialog.kind === 'edit-folder' ? descendantsOf(tree.folders, dialog.folder.id) : new Set<string>()
  const folders = [...tree.folders].filter((f) => !excluded.has(f.id)).sort((a, b) => a.path.localeCompare(b.path, locale))

  const action = useMutation({
    mutationFn: async (): Promise<void> => {
      switch (dialog.kind) {
        case 'new-doc': {
          const docName = asDocName(name)
          const title = docName.replace(/\.md$/i, '').replace(/[-_]+/g, ' ').trim()
          const created = await api.docs.create({ folderId: folder, name: docName, content: `# ${title}\n\n` })
          onCreatedDoc(created)
          return
        }
        case 'new-folder': {
          const created = await api.docs.createFolder({ name: name.trim(), parentId: folder })
          onChanged(created.id)
          return
        }
        case 'move-doc':
          await api.docs.move(dialog.doc.id, { folderId: folder, name: asDocName(name), moveToRoot: folder === null })
          onChanged(folder)
          return
        case 'edit-folder':
          await api.docs.updateFolder(dialog.folder.id, { name: name.trim(), parentId: folder, moveToRoot: folder === null })
          onChanged(dialog.folder.id)
          return
        case 'delete-doc':
          await api.docs.remove(dialog.doc.id)
          onDeletedDoc()
          return
        case 'delete-folder':
          await api.docs.removeFolder(dialog.folder.id)
          onChanged(dialog.folder.parentId)
          return
      }
    },
  })

  const deleting = dialog.kind === 'delete-doc' || dialog.kind === 'delete-folder'
  const forDoc = dialog.kind === 'new-doc' || dialog.kind === 'move-doc'
  const valid = deleting || (forDoc ? isValidDocName(asDocName(name)) : isValidSegment(name.trim()))

  const title = {
    'new-doc': t('docs.newDoc'),
    'new-folder': t('docs.newFolder'),
    'move-doc': t('docs.moveTitle'),
    'edit-folder': t('docs.editFolder'),
    'delete-doc': t('docs.deleteTitle'),
    'delete-folder': t('docs.deleteFolder'),
  }[dialog.kind]

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    if (valid && !action.isPending) action.mutate()
  }

  return (
    <div
      className="docs-dialog__backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <form className="panel docs-dialog" role="dialog" aria-modal="true" aria-label={title} onSubmit={submit}>
        <header className="panel__header">
          <h2 className="panel__title">{title}</h2>
          <div className="spacer" />
          <button type="button" className="btn btn--sm btn--ghost btn--icon" onClick={onClose} aria-label={t('common.close')}>
            <X size={14} />
          </button>
        </header>
        <div className="panel__body stack">
          {dialog.kind === 'delete-doc' && <p className="small">{t('docs.deleteConfirm', { path: dialog.doc.path })}</p>}
          {dialog.kind === 'delete-folder' && <p className="small">{t('docs.deleteFolderConfirm', { path: dialog.folder.path })}</p>}

          {!deleting && (
            <label className="field">
              <span className="field__label">{forDoc ? t('docs.docName') : t('docs.folderName')}</span>
              <input
                className="input"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={forDoc ? t('docs.docNamePlaceholder') : t('docs.folderNamePlaceholder')}
                autoFocus
                maxLength={120}
              />
              <span className="field__hint">{forDoc ? t('docs.docNameHint') : t('docs.folderNameHint')}</span>
            </label>
          )}

          {!deleting && (
            <label className="field">
              <span className="field__label">{dialog.kind === 'new-folder' || dialog.kind === 'edit-folder' ? t('docs.parent') : t('docs.folder')}</span>
              <select className="select" value={folder ?? ''} onChange={(event) => setFolder(event.target.value || null)}>
                <option value="">{t('docs.root')}</option>
                {folders.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.path}
                  </option>
                ))}
              </select>
            </label>
          )}

          {action.error && <span className="field__error">{errorText(action.error)}</span>}

          <div className="row row--wrap" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn--sm btn--ghost" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className={`btn btn--sm ${deleting ? 'btn--danger' : 'btn--primary'}`} disabled={!valid || action.isPending}>
              {deleting ? t('common.delete') : dialog.kind === 'move-doc' || dialog.kind === 'edit-folder' ? t('common.save') : t('common.create')}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
